<template>
  <Modal ref="modalRef" width="30rem">
    <template #header>Add {{ name }} to portfolios</template>

    <template #body>
      <View direction="column" gap="md">
        <Text variant="body-medium" color="secondary">
          They join each one as a collaborator: every property in it, and the ones added later. They'll be able to
          change those properties and see them on their dashboard.
        </Text>
        <PortfolioChecklist v-model="portfolioIds" :portfolios="portfolios" :locked-reasons="lockedReasons" />
      </View>
    </template>

    <template #footer>
      <View gap="sm" justify="end">
        <Button variant="ghost" @click="close">Cancel</Button>
        <Button :disabled="!portfolioIds.length" :loading="isSubmitting" @click="add">Add</Button>
      </View>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { useQueryCache } from '@pinia/colada';
import pluralize from 'pluralize';
import { computed, ref } from 'vue';

import { Button, Modal, Text, View, useAlertsStore } from '@tallo/design-system';
import {
  CompanyTeamMemberDto,
  teamMemberName as nameOf,
  useAddMemberToPortfolios,
  useCompanyPortfoliosQuery,
} from '@tallo/investor/company';
import { accessRequestsToAnswerQueryKey, isPropertyTeamQuery } from '@tallo/investor/property';
import { escapeHtml } from '@tallo/utility';

import PortfolioChecklist from './portfolio-checklist.vue';

const modalRef = ref<InstanceType<typeof Modal>>();
const member = ref<CompanyTeamMemberDto>();
const portfolioIds = ref<string[]>([]);
const alertsStore = useAlertsStore();
const queryCache = useQueryCache();
const addToPortfolios = useAddMemberToPortfolios();
const isSubmitting = addToPortfolios.isLoading;
const { portfolios } = useCompanyPortfoliosQuery();

const name = computed(() => (member.value ? nameOf(member.value) : ''));

// Owning a portfolio or already joining its new properties leaves nothing to add
const lockedReasons = computed(() => {
  const investorId = member.value?.investorId;
  const reasons: Record<string, string> = {};

  for (const portfolio of portfolios.value) {
    if (portfolio.ownerId === investorId) {
      reasons[portfolio.id] = 'They own it';
    } else if (portfolio.collaborators.some((entry) => entry.investorId === investorId && entry.joinsNewUnits)) {
      reasons[portfolio.id] = 'Already a collaborator';
    }
  }

  return reasons;
});

function open(target: CompanyTeamMemberDto) {
  member.value = target;
  portfolioIds.value = [];
  modalRef.value?.show();
}

function close() {
  modalRef.value?.close();
}

async function add() {
  if (!member.value || !portfolioIds.value.length) return;

  const count = portfolioIds.value.length;

  try {
    await addToPortfolios.mutateAsync({ member: member.value, portfolioIds: portfolioIds.value });
    // Each unit's Team tab lists its collaborators, and joining answers any request the seat had open there
    queryCache.invalidateQueries({ predicate: isPropertyTeamQuery }).catch(() => undefined);
    queryCache.invalidateQueries({ key: [accessRequestsToAnswerQueryKey] }).catch(() => undefined);
    close();
    alertsStore.success(`<b>${escapeHtml(name.value)}</b> joined ${pluralize('portfolio', count, true)}`);
  } catch {
    // Already reported by the mutation's toast; the dialog stays open for another try
  }
}

defineExpose({ open });
</script>
